import ReactGA from 'react-ga4';
import mixpanel from 'mixpanel-browser';
import axios from 'axios';
import { getUserData, getPortalType } from '@/utils/auth';

// Initialize GA with measurement id (from env)
export const initGA = (measurementId) => {
  try {
    if (!measurementId) return;
    ReactGA.initialize(measurementId);
    // Optional: enable debug when not production
    if (process.env.NODE_ENV !== 'production') {
      // no-op: react-ga4 has internal debug via browser extensions
    }
  } catch (e) {
    // fail silently
    // console.warn('GA init failed', e);
  }
};

// Initialize Mixpanel if token available
export const initMixpanel = (token) => {
  try {
    if (!token) return;
    mixpanel.init(token, { debug: process.env.NODE_ENV !== 'production', track_pageview: false });
    // Install lightweight axios interceptors to capture important API events centrally
    try {
      // avoid double-installing
      if (!axios.__analytics_interceptor_installed) {
        const reqTimes = new Map();

        axios.interceptors.request.use((config) => {
          try {
            const key = `${config.method || 'get'}::${config.url}` + Math.random();
            reqTimes.set(key, Date.now());
            // attach the key onto config so response can find it (best-effort)
            config.__analytics_key = key;
          } catch (e) {}
          return config;
        }, (err) => Promise.reject(err));

        axios.interceptors.response.use((resp) => {
          try {
            const config = resp.config || {};
            const key = config.__analytics_key;
            const start = key ? reqTimes.get(key) : null;
            const duration = start ? Math.max(0, Date.now() - start) : null;
            const method = (config.method || 'get').toUpperCase();
            const url = config.url || resp.request?.responseURL || '';
            // Only track mutating endpoints to reduce volume
            if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
              const evt = {
                api_method: method,
                api_url: url,
                status: resp.status,
                duration_ms: duration,
              };
              try { trackEvent('api_request', attachUserProps(evt)); } catch (e) {}
            }
            if (key) reqTimes.delete(key);
          } catch (e) {}
          return resp;
        }, (error) => {
          try {
            const config = error.config || {};
            const method = (config.method || 'get').toUpperCase();
            const url = config.url || '';
            const status = error.response?.status || null;
            const key = config.__analytics_key;
            const start = key ? reqTimes.get(key) : null;
            const duration = start ? Math.max(0, Date.now() - start) : null;
            if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
              const evt = {
                api_method: method,
                api_url: url,
                status,
                duration_ms: duration,
                error: String(error.message || error),
              };
              try { trackEvent('api_error', attachUserProps(evt)); } catch (e) {}
            }
            if (key) reqTimes.delete(key);
          } catch (e) {}
          return Promise.reject(error);
        });

        axios.__analytics_interceptor_installed = true;
      }
    } catch (e) {
      // swallow interceptor failures
    }
  } catch (e) {
    // fail silently
  }
};

export const trackPageView = (path) => {
  try {
    if (!path) return;
    ReactGA.send({ hitType: 'pageview', page: path });
    try {
      if (mixpanel && mixpanel.track) {
        mixpanel.track('Page View', { page: path });
      }
    } catch (e) {}
  } catch (e) {}
};

export const trackEvent = (name, params = {}) => {
  try {
    if (!name) return;
    // react-ga4: send event name and params
    // prefer the (name, params) signature when available
    try {
      ReactGA.event(name, params);
    } catch (e) {
      // fallback to older object form
      ReactGA.event({ action: name, ...params });
    }
    try {
      if (mixpanel && mixpanel.track) mixpanel.track(name, params);
    } catch (e) {}
  } catch (e) {}
};

// Attach user properties from local storage user profile
export const attachUserProps = (params = {}) => {
  try {
    const portal = getPortalType(window.location.pathname || '');
    const user = getUserData(portal) || {};

    const userProps = {
      user_id: user.id ?? user.user_id ?? user.pk ?? null,
      user_type: user.user_type ?? user.type ?? portal ?? null,
      username: user.username ?? user.name ?? user.full_name ?? null,
      school_id: user.school_id ?? user.school_code ?? user.school?.id ?? null,
      country: user.country ?? user.country_code ?? null,
      portal,
    };

    const merged = { ...params, ...userProps };

    // Inform Mixpanel of the current identity (best-effort, idempotent)
    try {
      if (mixpanel && mixpanel.identify && userProps.user_id) {
        try {
          mixpanel.identify(String(userProps.user_id));
        } catch (e) {}
        try {
          mixpanel.people.set(userProps);
        } catch (e) {}
      }
    } catch (e) {}

    return merged;
  } catch (e) {
    return params;
  }
};

// Convenience wrapper to track content events with user props
export const trackContentEvent = (name, contentParams = {}) => {
  const payload = attachUserProps(contentParams);
  trackEvent(name, payload);
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  attachUserProps,
  trackContentEvent,
};
